import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Scale, AlertTriangle, CheckCircle, XCircle, DollarSign, Globe, Gavel } from 'lucide-react';

const TermsOfService: React.FC = () => {
  const sections = [
    {
      icon: CheckCircle,
      title: "Acceptance of Terms",
      content: [
        "By accessing our website, you agree to these Terms of Service",
        "You must be at least 13 years old to use our services",
        "You are responsible for maintaining account security",
        "You agree to provide accurate and complete information"
      ]
    },
    {
      icon: Globe,
      title: "Use of Services",
      content: [
        "Access to sports data and analytics",
        "Personal, non-commercial use only",
        "No automated data scraping or harvesting",
        "Respect for intellectual property rights"
      ]
    },
    {
      icon: XCircle,
      title: "Prohibited Activities",
      content: [
        "Violating any applicable laws or regulations",
        "Impersonating others or providing false information",
        "Interfering with service operations",
        "Attempting unauthorized access to systems"
      ]
    },
    {
      icon: Scale,
      title: "Limitation of Liability",
      content: [
        "Services provided 'as is' without warranties",
        "No guarantee of data accuracy or completeness",
        "Limited liability for service interruptions",
        "User assumes risk of service usage"
      ]
    },
    {
      icon: DollarSign,
      title: "Subscriptions and Payments",
      content: [
        "Free tier with limited access",
        "Premium subscriptions for advanced features",
        "Automatic renewal unless cancelled",
        "Refund policy as per subscription terms"
      ]
    },
    {
      icon: Gavel,
      title: "Governing Law",
      content: [
        "Governed by laws of Kansas, United States",
        "Disputes resolved through binding arbitration",
        "Class action waiver applies",
        "Venue in Johnson County, Kansas"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Terms of Service</h1>
            <p className="text-gray-400">Effective Date: {new Date().toLocaleDateString()}</p>
          </div>

          {/* Introduction */}
          <div className="bg-gray-800/50 rounded-lg p-6 mb-8">
            <p className="text-gray-300 leading-relaxed mb-4">
              Welcome to The Sports Machine Network. These Terms of Service ("Terms") govern your use of our website,
              services, and applications. By accessing or using our services, you agree to be bound by these Terms.
            </p>
            <div className="flex items-center space-x-2 text-amber-400">
              <AlertTriangle className="w-5 h-5" />
              <p className="font-semibold">Please read these terms carefully before using our services.</p>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => {
              const Icon = section.icon;
              return (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-800/30 rounded-lg p-6 border border-gray-700"
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-semibold text-white mb-4">{section.title}</h2>
                      <ul className="space-y-2">
                        {section.content.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-primary mr-2">•</span>
                            <span className="text-gray-300">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Modifications Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-12 bg-amber-500/10 rounded-lg p-6 border border-amber-500/20"
          >
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <h3 className="text-lg font-semibold text-white">Modifications to Terms</h3>
            </div>
            <p className="text-gray-300">
              We reserve the right to modify these Terms at any time. We will notify users of any material changes
              via email or through a notice on our website. Your continued use of our services after such modifications
              constitutes acceptance of the updated Terms.
            </p>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mt-8 bg-primary/10 rounded-lg p-6 border border-primary/20"
          >
            <h3 className="text-lg font-semibold text-white mb-3">Questions About Our Terms?</h3>
            <p className="text-gray-300 mb-3">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="space-y-2">
              <p className="text-gray-300">
                Email: <a href="mailto:legal@the-sports-machine.com" className="text-primary hover:text-primary-400">
                  legal@the-sports-machine.com
                </a>
              </p>
              <p className="text-gray-300">
                Address: The Sports Machine Network, Overland Park, KS 66210
              </p>
            </div>
          </motion.div>

          {/* Footer Note */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>
              By using The Sports Machine Network, you acknowledge that you have read, understood,
              and agree to be bound by these Terms of Service.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;